import { INDICATOR_PANE_MARGIN_PX } from "../constants";
import PriceScale from "../scales/PriceScale";
import { MAIN_PANE_ID } from "./indicatorTypes";
import { buildShownPlots } from "./shownPlots";
import { calculateAll, recalculateFrom } from "./calculation";
import { createIndicatorRenderer } from "./indicatorRenderer";
import { createAutoscaleMotion } from "../motion/autoscaleMotion";
import { mergeRanges, valueRange } from "./valueRange";

const PANE_MARGINS = { marginTop: INDICATOR_PANE_MARGIN_PX, marginBottom: INDICATOR_PANE_MARGIN_PX };

/**
 * One indicator on the chart: its inputs, styles, values for the shown bars
 * and their geometry. A new history recalculates everything; a new bar and
 * a tick (also every frame of the last bar animation, E5) only the tail,
 * and a tick rewrites only the last instances.
 *
 * @param {object} params
 * @param {string} params.key - unique on the chart
 * @param {object} params.definition - from the registry
 * @param {object} params.inputs - normalizeInputs
 * @param {object} params.styles - mergeStyles
 * @param {object} params.glLayer
 * @param {() => number} params.getPriceStep - price step of the pair (calculation context)
 */
export const createIndicatorInstance = ({
  key,
  definition,
  inputs: initialInputs,
  styles: initialStyles,
  glLayer,
  getPriceStep,
}) => {
  const renderer = createIndicatorRenderer(glLayer);
  // An oscillator has a pane with its own price scale
  const paneScale = definition.isOscillator ? new PriceScale(PANE_MARGINS) : null;
  // ...and its own smooth autoscale (layoutIndicatorPanes)
  const paneMotion = definition.isOscillator ? createAutoscaleMotion() : null;
  let inputs = initialInputs;
  let styles = initialStyles;
  let isVisible = true;
  let bars = [];
  const context = () => ({ priceStep: getPriceStep() });
  let series = calculateAll(definition, bars, inputs, context());
  // Visible plots with parsed colours and shifts (the renderer, the autoscale); set by rebuild
  let shownPlots = null;

  const rebuild = () => {
    shownPlots = buildShownPlots(definition, styles, inputs);
    renderer.rebuild({ plots: shownPlots, series, bars });
  };

  rebuild();

  return {
    key,
    definition,
    paneScale,
    paneMotion,

    get paneId() {
      return definition.isOscillator ? key : MAIN_PANE_ID;
    },

    get inputs() {
      return inputs;
    },

    get styles() {
      return styles;
    },

    get isVisible() {
      return isVisible;
    },

    /** Values per plot key, NaN where there is none. */
    get series() {
      return series;
    },

    setVisible: (nextIsVisible) => {
      isVisible = nextIsVisible;
    },

    setInputs: (nextInputs) => {
      inputs = nextInputs;
      series = calculateAll(definition, bars, inputs, context());
      rebuild();
    },

    setStyles: (nextStyles) => {
      styles = nextStyles;
      rebuild();
    },

    /**
     * @param {object[]} nextBars - shown bars (animator)
     * @param {boolean} isNewBar - one bar was added on the right
     */
    setBars: (nextBars, isNewBar) => {
      const previousLength = bars.length;

      bars = nextBars;

      if (isNewBar && previousLength > 0 && nextBars.length === previousLength + 1) {
        recalculateFrom(definition, bars, inputs, series, previousLength - 1, context());
      } else {
        series = calculateAll(definition, bars, inputs, context());
      }

      rebuild();
    },

    /** @param {object[]} nextBars - the same bars, the last one moved */
    updateLastBar: (nextBars) => {
      bars = nextBars;

      if (!bars.length) return;

      const firstChanged = recalculateFrom(definition, bars, inputs, series, bars.length - 1, context());

      renderer.updateFrom({ series, bars }, firstChanged);
    },

    /** Values of the visible levels (labels on the scale of the pane); none while the indicator is hidden. */
    get levelValues() {
      return isVisible ? shownPlots.levels.lines.map((item) => item.value) : [];
    },

    /** Range of the visible plots over bars first..last and of the levels, for the autoscale of its pane. */
    priceRange: (first, last) => {
      if (!isVisible) return null;

      return mergeRanges([valueRange(series, shownPlots.valuePlots, first, last), shownPlots.levels.range]);
    },

    /** @param {object} params - see indicatorRenderer.draw */
    draw: (params) => {
      if (isVisible) renderer.draw(params);
    },

    release: renderer.release,
  };
};
